import React, { Component } from 'react';
import styles from './UploadComponent.module.scss';
import Icon from '@ant-design/icons';
import { ReactComponent as CloudUpload } from '../../../../../resources/images/cloud-upload.svg';
import { ReactComponent as TrashCanSvg } from '../../../../../resources/images/trash-can.svg';
import { ReactComponent as AttachmentSvg } from '../../../../../resources/images/attachment.svg';
import CustomContext from '../../../../../context/CustomContext';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button, Col, Form, FormInstance, List, Row, Space, Upload } from 'antd';
import { Link } from 'react-router-dom';
import TextArea from 'antd/lib/input/TextArea';
import memberApi from '../../../../../api/MemberApi';
import notificationService from '../../../../../services/NotificationService';
import { Member } from '../../../../../model/entities';
import { UploadFile } from 'antd/lib/upload/interface';
import { RuleObject, StoreValue } from 'rc-field-form/lib/interface';
import FileSizeComponent from '../../../../../components/FileSizeComponent/FileSizeComponent';

class UploadComponent extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();
    readonly maxFileSize = 20 * 1024 * 1024;

    constructor(props: Props) {
        super(props);
        this.state = { files: [] };
    }

    /** METHODS **/

    load = async (values: any) => {
        const { files } = this.state;

        try {
            this.setState({ loading: true });
            let members: Member[];
            if (files.length > 0) {
                const file: any = files[0];
                members = await memberApi.loadSpreadsheet(file);
            } else {
                const csv: string = values.text;
                members = await memberApi.loadCsv(csv);
            }
            this.props.onLoadMembers(members);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    uploadFile = (file: UploadFile) => {
        const files: UploadFile[] = [];
        if (!this.isFileSizeValid(file)) {
            this.setState({ files });
        } else {
            files.push(file);
            this.setState({ files });
        }

        return false;
    };

    isFileSizeValid = (file: UploadFile) => !file.size || file.size <= this.maxFileSize;

    validateFile = (rule: RuleObject, value: StoreValue, callback: (error?: string) => void): Promise<void> | void => {
        if (value && value.file && !this.isFileSizeValid(value.file)) {
            callback(this.props.intl.formatMessage({ id: 'status.file.size' }));
        }
        callback();
    };

    removeFile = () => {
        const files: UploadFile[] = [];
        this.formRef.current!.setFieldsValue({
            file: files,
        });
        this.setState({ files });
    };

    /*** COMPONENTS ***/

    renderForm = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const { files, loading } = this.state;

        return (
            <Form ref={this.formRef} onFinish={this.load} colon={false} layout="vertical">
                <Form.Item label={<FormattedMessage id="members.import.steps.1.text" />} name="text">
                    <TextArea
                        maxLength={10000}
                        size="large"
                        rows={8}
                        placeholder={intl.formatMessage({ id: 'members.import.steps.1.text.placeholder' })}
                        disabled={files.length > 0}
                    />
                </Form.Item>
                <Row>
                    <Col span={8}>
                        <Form.Item
                            name="file"
                            valuePropName="files"
                            label={
                                <>
                                    <span>
                                        <FormattedMessage id="members.import.steps.1.upload" />
                                    </span>
                                    <a
                                        href={`${process.env.PUBLIC_URL}/members-import-template.xlsx`}
                                        download={intl.formatMessage({ id: 'members.import.steps.1.upload.file' })}
                                        className={styles.link}
                                    >
                                        <FormattedMessage id="members.import.steps.1.upload.link" />
                                    </a>
                                </>
                            }
                            rules={[
                                {
                                    validator: this.validateFile,
                                },
                            ]}
                            extra={
                                <>
                                    <FormattedMessage id="attachment.size" />{' '}
                                    <FileSizeComponent value={this.maxFileSize} />
                                </>
                            }
                        >
                            <Upload.Dragger beforeUpload={this.uploadFile} fileList={files} showUploadList={false}>
                                <Icon component={CloudUpload} />{' '}
                                <FormattedMessage id="members.import.steps.1.upload.button" />
                            </Upload.Dragger>
                        </Form.Item>
                    </Col>
                    <Col span={12}>{this.renderFiles()}</Col>
                </Row>

                <Form.Item className="buttons">
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            icon={<Icon component={CloudUpload} />}
                            loading={loading}
                        >
                            <FormattedMessage id="button.load" tagName="span" />
                        </Button>
                        <Link to="/setup/members">
                            <Button type="text" size="large">
                                <FormattedMessage id="button.back" tagName="span" />
                            </Button>
                        </Link>
                    </Space>
                </Form.Item>
            </Form>
        );
    };

    renderFiles = (): React.ReactElement | undefined => {
        const { files } = this.state;

        return (
            <List
                className={styles.files}
                itemLayout="horizontal"
                dataSource={files}
                locale={{ emptyText: <></> }}
                renderItem={(file) => (
                    <List.Item
                        actions={[
                            <Button
                                icon={<Icon component={TrashCanSvg} />}
                                danger
                                size="small"
                                onClick={this.removeFile}
                            />,
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<Icon component={AttachmentSvg} />}
                            title={file.name}
                            description={<FileSizeComponent value={file.size} />}
                        />
                    </List.Item>
                )}
            />
        );
    };

    render() {
        return this.renderForm();
    }
}
export default injectIntl(UploadComponent);

interface Props extends WrappedComponentProps {
    onLoadMembers: (members: Member[]) => void;
}

interface State {
    files: UploadFile[];
    loading?: boolean;
}
